var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import isEqual from 'lodash/isEqual';
import { CannotReadNextValueError } from './errors/CannotReadNextValueError';
import { NotEnoughColumnsError } from './errors/NotEnoughColumnsError';
import { UnexpectedColumnError } from './errors/UnexpectedColumnError';
import { ValueValidationError } from './errors/ValueValidationError';
var PROBABLY_INCOMPLETE_DATA = Symbol('PROBABLY_INCOMPLETE_DATA');
var CSV_END_OF_VALUE = 'CSV_END_OF_VALUE';
var CSV_END_OF_ROW = 'CSV_END_OF_ROW';
var readNextValue = function (input, pos, _a) {
    var rowDelimiter = _a.rowDelimiter, separator = _a.separator, quoteCharacter = _a.quoteCharacter, quoteMode = _a.quoteMode, escapeCharacter = _a.escapeCharacter, isEndOfStream = _a.isEndOfStream;
    var isAutoQuote = quoteMode === 'auto';
    var shouldBeQuoted = quoteMode === 'required';
    var isQuoted = shouldBeQuoted;
    var startPos = pos;
    if (startPos > input.length) {
        throw new Error('CSV fully read, nothing left');
    }
    if (shouldBeQuoted) {
        if (input[startPos] === quoteCharacter) {
            startPos += 1;
        }
        else if (input.length > 0) {
            throw new Error('Expected quoted CSV, but got unquoted');
        }
    }
    if (isAutoQuote) {
        if (input[startPos] === quoteCharacter) {
            startPos += 1;
            isQuoted = true;
        }
    }
    for (var i = startPos; i < input.length; i++) {
        var char = input[i];
        var nextCharIndex = i + 1;
        if (char === escapeCharacter) {
            // as next symbol escaped, we just skip it
            if (escapeCharacter !== quoteCharacter ||
                input[nextCharIndex] === escapeCharacter) {
                i = nextCharIndex;
                continue;
            }
        }
        if (isQuoted) {
            var nextCharAfterQuoteIndex = nextCharIndex + 1;
            if (char === quoteCharacter) {
                var nextChar = input[nextCharIndex];
                var endRowDelimiterIndex = nextCharIndex + rowDelimiter.length;
                if (nextCharIndex === input.length) {
                    if (isEndOfStream) {
                        return {
                            value: input.substring(startPos, i),
                            nextPos: nextCharAfterQuoteIndex,
                            type: CSV_END_OF_ROW,
                        };
                    }
                    return PROBABLY_INCOMPLETE_DATA;
                }
                else if (rowDelimiter.startsWith(nextChar) &&
                    input.substring(nextCharIndex, endRowDelimiterIndex) === rowDelimiter) {
                    return {
                        value: input.substring(startPos, i),
                        nextPos: endRowDelimiterIndex,
                        type: CSV_END_OF_ROW,
                    };
                }
                else if (nextChar === separator) {
                    return {
                        value: input.substring(startPos, i),
                        nextPos: nextCharAfterQuoteIndex,
                        type: CSV_END_OF_VALUE,
                    };
                }
                throw new Error('CSV quote is ended, but no separator found');
            }
            else {
                continue;
            }
        }
        if (char === separator) {
            return {
                value: input.substring(startPos, i),
                nextPos: nextCharIndex,
                type: CSV_END_OF_VALUE,
            };
        }
        else if (rowDelimiter.startsWith(char) &&
            input.substring(i, i + rowDelimiter.length) === rowDelimiter) {
            return {
                value: input.substring(startPos, i),
                nextPos: i + rowDelimiter.length,
                type: CSV_END_OF_ROW,
            };
        }
    }
    if (isEndOfStream) {
        if (isQuoted) {
            throw new Error('CSV quoted value has no enclosed quote');
        }
        return {
            value: input.substring(startPos),
            nextPos: input.length + 1,
            type: CSV_END_OF_ROW,
        };
    }
    return PROBABLY_INCOMPLETE_DATA;
};
var readValues = function (input, initialPos, options, rowPos) {
    var expectedLength = options.expectedLength;
    var startPos = initialPos;
    var values = new Array(expectedLength);
    for (var i = 0; i < expectedLength; i++) {
        var isLast = expectedLength - 1 === i;
        var result = readNextValue(input, startPos, options);
        if (result === PROBABLY_INCOMPLETE_DATA) {
            return PROBABLY_INCOMPLETE_DATA;
        }
        else if (result.type === CSV_END_OF_VALUE) {
            if (!isLast) {
                values[i] = result.value;
                startPos = result.nextPos;
            }
            else {
                throw new UnexpectedColumnError(expectedLength, rowPos);
            }
        }
        else if (result.type === CSV_END_OF_ROW) {
            if (isLast) {
                values[i] = result.value;
                startPos = result.nextPos;
            }
            else {
                throw new NotEnoughColumnsError(expectedLength, i + 1, rowPos);
            }
        }
    }
    return { values: values, nextPos: startPos };
};
var MAX_FORMATTED_HEADER_LENGTH = 100;
var formatHeaderValues = function (values) {
    var valuesDetails = JSON.stringify(values);
    if (valuesDetails.length > MAX_FORMATTED_HEADER_LENGTH) {
        valuesDetails =
            valuesDetails.substring(0, MAX_FORMATTED_HEADER_LENGTH) + '...]';
    }
    return valuesDetails;
};
var lookupForEndOfRow = function (input, _a) {
    var quoteMode = _a.quoteMode, quoteCharacter = _a.quoteCharacter, escapeCharacter = _a.escapeCharacter;
    var isQuoted = false;
    for (var i = 0; i < input.length; i++) {
        var char = input[i];
        if (isQuoted && char === escapeCharacter) {
            continue;
        }
        if (quoteMode !== 'none') {
            if (char === quoteCharacter) {
                isQuoted = !isQuoted;
            }
        }
        if (!isQuoted) {
            if (char === "\n") {
                if (input[i - 1] === "\r") {
                    return "\r\n";
                }
                return char;
            }
        }
    }
    return '\n';
};
export var buildCsvValidator = function (schema, _a) {
    var _b = _a === void 0 ? {} : _a, _c = _b.errorExtractor, errorExtractor = _c === void 0 ? function (err) { return [err ? err.toString() : 'Unknown']; } : _c, _d = _b.rowDelimiter, rowDelimiter = _d === void 0 ? 'auto' : _d, _e = _b.separator, separator = _e === void 0 ? ',' : _e, _f = _b.quoteMode, quoteMode = _f === void 0 ? 'auto' : _f, _g = _b.quoteCharacter, quoteCharacter = _g === void 0 ? "\"" : _g, _h = _b.escapeCharacter, escapeCharacter = _h === void 0 ? '"' : _h, _j = _b.maxChunksAllowedBeforeFail, maxChunksAllowedBeforeFail = _j === void 0 ? 2 : _j, _k = _b.caseInsensitiveHeader, caseInsensitiveHeader = _k === void 0 ? false : _k, _l = _b.validationMode, validationMode = _l === void 0 ? 'stopOnFirstFailure' : _l;
    var isHeader = !Array.isArray(schema);
    var valuesSchema = Object.values(schema);
    var expectedLength = valuesSchema.length;
    var isStopOnFirstFailure = validationMode === 'stopOnFirstFailure';
    var valueReadOptions = {
        rowDelimiter: rowDelimiter,
        separator: separator,
        quoteMode: quoteMode,
        quoteCharacter: quoteCharacter,
        expectedLength: expectedLength,
        escapeCharacter: escapeCharacter,
    };
    var lastChunkData = '';
    var rowScanned = 0;
    var chunkWithoutCompleteValues = 0;
    return function (_a) {
        var value = _a.value, done = _a.done;
        var startPos = 0;
        var errors = [];
        var csvValidatorInfo = {
            errors: errors,
        };
        if (rowScanned === 0 && value && rowDelimiter === 'auto') {
            valueReadOptions.rowDelimiter = lookupForEndOfRow(value, valueReadOptions);
        }
        if (done && !value) {
            if (!lastChunkData) {
                return {
                    errors: errors,
                };
            }
        }
        var input = lastChunkData + value;
        var result;
        valueReadOptions.isEndOfStream = done;
        while ((result = readValues(input, startPos, valueReadOptions, rowScanned))) {
            if (result === PROBABLY_INCOMPLETE_DATA) {
                if (chunkWithoutCompleteValues === maxChunksAllowedBeforeFail) {
                    throw new CannotReadNextValueError(valueReadOptions.rowDelimiter, input.length - startPos);
                }
                if (done) {
                    throw new Error('Stream end, but cannot properly parse');
                }
                else {
                    lastChunkData = input.substring(startPos);
                    chunkWithoutCompleteValues += 1;
                    return csvValidatorInfo;
                }
            }
            rowScanned += 1;
            chunkWithoutCompleteValues = 0;
            var values = result.values;
            if (isHeader) {
                var expectedHeaderValues = Object.keys(schema);
                var actualHeaderValues = values;
                if (caseInsensitiveHeader) {
                    expectedHeaderValues = expectedHeaderValues.map(function (header) {
                        return header.toLowerCase();
                    });
                    actualHeaderValues = actualHeaderValues.map(function (header) {
                        return header.toLowerCase();
                    });
                }
                if (!isEqual(actualHeaderValues, expectedHeaderValues)) {
                    throw new Error('Unexpected column headers, should be ' +
                        formatHeaderValues(expectedHeaderValues) +
                        ', but got ' +
                        formatHeaderValues(actualHeaderValues));
                }
                isHeader = false;
            }
            else {
                // validate values
                if (valuesSchema.length !== values.length) {
                    throw new NotEnoughColumnsError(valuesSchema.length, values.length, rowScanned);
                }
                for (var i = 0; i < valuesSchema.length; i++) {
                    try {
                        valuesSchema[i](values[i]);
                    }
                    catch (err) {
                        var validationError = new ValueValidationError(values[i], errorExtractor(err), values, rowScanned);
                        if (isStopOnFirstFailure) {
                            throw validationError;
                        }
                        else {
                            errors.push(validationError);
                        }
                    }
                }
            }
            startPos = result.nextPos;
            if (startPos >= input.length) {
                // clean lastChunkData as it may prepend to the next chunk wrong data
                lastChunkData = '';
                return csvValidatorInfo;
            }
        }
        return csvValidatorInfo;
    };
};
export var createValidateCsvStream = function (_a) {
    var brakeFn = _a.brakeFn;
    return function (stream, validator, _a) {
        var _b = _a === void 0 ? {} : _a, _c = _b.maxErrorsCount, maxErrorsCount = _c === void 0 ? 100 : _c;
        var events = new EventTarget();
        var errors = [];
        var totalErrorsCount = 0;
        var whenComplete = new Promise(function (resolve, reject) {
            var process = function () { return __awaiter(void 0, void 0, void 0, function () {
                var reader, result, proceed, validatorResult;
                var _a, _b, _c;
                return __generator(this, function (_d) {
                    switch (_d.label) {
                        case 0:
                            reader = stream.getReader();
                            proceed = 0;
                            _d.label = 1;
                        case 1: return [4 /*yield*/, reader.read()];
                        case 2:
                            if (!(result = _d.sent())) return [3 /*break*/, 4];
                            validatorResult = validator({
                                done: result.done,
                                value: (_a = result.value) !== null && _a !== void 0 ? _a : '',
                            });
                            totalErrorsCount += validatorResult.errors.length;
                            if (errors.length < maxErrorsCount) {
                                errors = errors
                                    .concat(validatorResult.errors)
                                    .slice(0, maxErrorsCount);
                            }
                            if (result.done) {
                                return [2 /*return*/, Promise.resolve({
                                        isValid: errors.length === 0,
                                        proceed: proceed,
                                        totalErrorsCount: totalErrorsCount,
                                        hasMoreErrorsThanTheLimit: totalErrorsCount > errors.length,
                                        errorSamples: errors,
                                    })];
                            }
                            proceed += (_c = (_b = result.value) === null || _b === void 0 ? void 0 : _b.length) !== null && _c !== void 0 ? _c : 0;
                            return [4 /*yield*/, new Promise(function (resolve) {
                                    events.dispatchEvent(new CustomEvent('progress', {
                                        detail: {
                                            isValid: errors.length === 0,
                                            proceed: proceed,
                                            totalErrorsCount: totalErrorsCount,
                                            hasMoreErrorsThanTheLimit: totalErrorsCount > errors.length,
                                            errorSamples: errors,
                                        },
                                    }));
                                    brakeFn(resolve);
                                })];
                        case 3:
                            _d.sent();
                            return [3 /*break*/, 1];
                        case 4: throw new Error('Unreachable code');
                    }
                });
            }); };
            process().then(resolve, reject);
        });
        return {
            events: events,
            whenComplete: whenComplete,
        };
    };
};
//# sourceMappingURL=streamCsvValidation.js.map